import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";

/**
 * Main app shell: sidebar + topbar + content area.
 * Desktop (≥1024px): static collapsible sidebar.
 * Mobile (<1024px): sidebar as slide-in overlay drawer.
 */
export function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024,
  );
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  // Track viewport to toggle between desktop sidebar and mobile overlay
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    setIsDesktop(mql.matches);
    const handler = (e: MediaQueryListEvent) => {
      setIsDesktop(e.matches);
      if (e.matches) setMobileOpen(false);
    };
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  // Scroll to top and close mobile sidebar on route change
  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
    setMobileOpen(false);
  }, [pathname]);

  // Lock body scroll when mobile sidebar is open (prevents iOS scroll-through)
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // On mobile the sidebar is always "expanded" (never collapsed)
  const sidebarCollapsed = isDesktop ? collapsed : false;

  return (
    <div className="flex h-dvh overflow-hidden bg-background">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setCollapsed(!collapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Topbar onMenuToggle={() => setMobileOpen((prev) => !prev)} />
        <main
          ref={mainRef}
          className={`flex-1 overflow-y-auto p-4 md:p-6${mobileOpen ? " overflow-hidden" : ""}`}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
